import { useState, useEffect } from 'react';

interface Vehicle {
  id: string;
  make: string;
  model: string;
  year: number;
  image: string;
  location: string;
  latitude: number;
  longitude: number;
  owner: string;
  mileage: number;
  price?: number;
}

interface VehicleMapViewProps {
  vehicles: Vehicle[];
  onVehicleSelect?: (vehicleId: string) => void;
}

export default function VehicleMapView({ vehicles, onVehicleSelect }: VehicleMapViewProps) {
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [mapCenter, setMapCenter] = useState({ lat: 40.7128, lng: -74.0060 });
  const [zoom, setZoom] = useState(10);

  useEffect(() => {
    // Get user's current location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userPos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setUserLocation(userPos);
          setMapCenter(userPos);
        },
        (error) => {
          console.log('Location access denied:', error);
        }
      );
    }
  }, []);

  const handleMarkerClick = (vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
    setMapCenter({ lat: vehicle.latitude, lng: vehicle.longitude });
    setZoom(13);
  };

  const handleRecenterMap = () => {
    if (userLocation) {
      setMapCenter(userLocation);
      setZoom(12);
    }
  };

  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
    const R = 3959; // Earth's radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return (R * c).toFixed(1);
  };

  const mapUrl = `https://www.google.com/maps/embed/v1/view?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&center=${mapCenter.lat},${mapCenter.lng}&zoom=${zoom}&maptype=roadmap`;

  return (
    <div className="relative w-full h-full">
      {/* Map Container */}
      <div className="absolute inset-0">
        <iframe
          src={mapUrl}
          className="w-full h-full border-0"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Vehicle Map View"
        />
      </div>

      {/* Map Controls */}
      <div className="absolute top-4 right-4 flex flex-col gap-2 z-10">
        {userLocation && (
          <button
            onClick={handleRecenterMap}
            className="w-10 h-10 flex items-center justify-center bg-white rounded-lg shadow-lg hover:bg-gray-50 transition-colors cursor-pointer"
            title="Center on my location"
          >
            <i className="ri-focus-3-line text-xl text-gray-700"></i>
          </button>
        )}
        <button
          onClick={() => setZoom(Math.min(zoom + 1, 18))}
          className="w-10 h-10 flex items-center justify-center bg-white rounded-lg shadow-lg hover:bg-gray-50 transition-colors cursor-pointer"
        >
          <i className="ri-add-line text-xl text-gray-700"></i>
        </button>
        <button
          onClick={() => setZoom(Math.max(zoom - 1, 3))}
          className="w-10 h-10 flex items-center justify-center bg-white rounded-lg shadow-lg hover:bg-gray-50 transition-colors cursor-pointer"
        >
          <i className="ri-subtract-line text-xl text-gray-700"></i>
        </button>
      </div>

      {/* Vehicle List Sidebar */}
      <div className="absolute left-4 top-4 bottom-4 w-80 bg-white rounded-xl shadow-2xl overflow-hidden z-10">
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-teal-500 to-teal-600">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <i className="ri-map-pin-line"></i>
              Vehicles Near You
            </h3>
            <p className="text-sm text-teal-50 mt-1">{vehicles.length} vehicles available</p>
          </div>

          {/* Vehicle List */}
          <div className="flex-1 overflow-y-auto">
            {vehicles.map((vehicle) => {
              const distance = userLocation 
                ? calculateDistance(userLocation.lat, userLocation.lng, vehicle.latitude, vehicle.longitude)
                : null;

              return (
                <div
                  key={vehicle.id}
                  onClick={() => handleMarkerClick(vehicle)}
                  className={`p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer ${
                    selectedVehicle?.id === vehicle.id ? 'bg-teal-50 border-l-4 border-l-teal-500' : ''
                  }`}
                >
                  <div className="flex gap-3">
                    <img
                      src={vehicle.image}
                      alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                      className="w-20 h-20 object-cover rounded-lg"
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-gray-900 truncate">
                        {vehicle.year} {vehicle.make} {vehicle.model}
                      </h4>
                      <div className="flex items-center gap-1 text-sm text-gray-600 mt-1">
                        <i className="ri-map-pin-2-fill text-teal-500"></i>
                        <span className="truncate">{vehicle.location}</span>
                      </div>
                      {distance && (
                        <div className="flex items-center gap-1 text-sm text-teal-600 font-medium mt-1">
                          <i className="ri-route-line"></i>
                          <span>{distance} miles away</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <i className="ri-dashboard-line"></i>
                          {vehicle.mileage.toLocaleString()} mi
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-user-line"></i>
                          {vehicle.owner}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer Info */}
          <div className="p-3 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <i className="ri-information-line text-teal-500"></i>
              <span>Click on a vehicle to view its location on the map</span>
            </div>
          </div>
        </div>
      </div>

      {/* Selected Vehicle Info Card */}
      {selectedVehicle && (
        <div className="absolute bottom-4 right-4 w-96 bg-white rounded-xl shadow-2xl overflow-hidden z-10">
          <div className="relative">
            <img
              src={selectedVehicle.image}
              alt={`${selectedVehicle.year} ${selectedVehicle.make} ${selectedVehicle.model}`}
              className="w-full h-48 object-cover"
            />
            <button
              onClick={() => setSelectedVehicle(null)}
              className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-gray-700"></i>
            </button>
          </div>
          
          <div className="p-4">
            <h3 className="text-xl font-bold text-gray-900">
              {selectedVehicle.year} {selectedVehicle.make} {selectedVehicle.model}
            </h3>
            
            <div className="flex items-center gap-2 mt-2 text-gray-600">
              <i className="ri-map-pin-2-fill text-teal-500"></i>
              <span className="text-sm">{selectedVehicle.location}</span>
            </div>

            {userLocation && (
              <div className="flex items-center gap-2 mt-2">
                <i className="ri-route-line text-teal-500"></i>
                <span className="text-sm font-medium text-teal-600">
                  {calculateDistance(
                    userLocation.lat,
                    userLocation.lng,
                    selectedVehicle.latitude,
                    selectedVehicle.longitude
                  )} miles from your location
                </span>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 mt-4">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <i className="ri-dashboard-line text-gray-400"></i>
                <span>{selectedVehicle.mileage.toLocaleString()} miles</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <i className="ri-user-line text-gray-400"></i>
                <span>{selectedVehicle.owner}</span>
              </div>
            </div>

            <div className="flex gap-2 mt-4">
              <button
                onClick={() => {
                  if (onVehicleSelect) {
                    onVehicleSelect(selectedVehicle.id);
                  }
                  window.REACT_APP_NAVIGATE(`/vehicle/${selectedVehicle.id}`);
                }}
                className="flex-1 px-4 py-2.5 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors font-medium whitespace-nowrap cursor-pointer"
              >
                View Details
              </button>
              <button
                onClick={() => {
                  window.open(
                    `https://www.google.com/maps/dir/?api=1&destination=${selectedVehicle.latitude},${selectedVehicle.longitude}`,
                    '_blank'
                  );
                }}
                className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium whitespace-nowrap cursor-pointer flex items-center gap-2"
              >
                <i className="ri-navigation-line"></i>
                Directions
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Location Indicator */}
      {userLocation && (
        <div className="absolute top-4 left-[22rem] bg-white px-4 py-2 rounded-lg shadow-lg z-10">
          <div className="flex items-center gap-2 text-sm">
            <div className="w-2 h-2 bg-teal-500 rounded-full animate-pulse"></div>
            <span className="text-gray-700 font-medium">Your Location</span>
          </div>
        </div>
      )}
    </div>
  );
}
